import React, { FC, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, updateDoc, collection, getDocs, query,
         where, orderBy, limit, addDoc, serverTimestamp, arrayUnion, arrayRemove } from 'firebase/firestore';
import { UserAuth } from '../../AuthContext';
import { Box, Flex, Text, Heading, Spinner, Center, Avatar } from '@chakra-ui/react';
import QRCode from 'qrcode';

const PCTDS_CONFIG = {
  none:     { label: 'Unverified',        color: '#6B7590', icon: '' },
  applied:  { label: 'Application Pending', color: '#D97706', icon: '⏳' },
  verified: { label: 'PCTDS Verified',    color: '#19E6CF', icon: '✅' },
};

const F = { ui: "'Rajdhani','Arial',sans-serif", mono: "'Share Tech Mono',monospace" };

const VenueProfilePage: FC = () => {
  const { venueId } = useParams<{ venueId: string }>();
  const { user } = UserAuth();
  const navigate = useNavigate();

  const [venue, setVenue] = useState<any>(null);
  const [tdProfile, setTdProfile] = useState<any>(null);
  const [upcomingEvents, setUpcomingEvents] = useState<any[]>([]);
  const [pastEvents, setPastEvents] = useState<any[]>([]);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<any>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!venueId) return;
      try {
        const venueSnap = await getDoc(doc(db, 'venues', venueId));
        if (!venueSnap.exists()) { setLoading(false); return; }
        const v = { id: venueId, ...venueSnap.data() };
        setVenue(v);
        setEditForm(v);
        setIsOwner(user?.uid === v.td_uid);
        setIsFollowing(v.followers?.includes(user?.uid));

        // Load TD profile
        if (v.td_uid) {
          const tdSnap = await getDoc(doc(db, 'users', v.td_uid));
          if (tdSnap.exists()) setTdProfile(tdSnap.data());
        }

        // Load upcoming events
        const now = new Date().toISOString();
        const upSnap = await getDocs(query(
          collection(db, 'events'),
          where('admin_uid', '==', v.td_uid),
          where('start', '>=', now),
          orderBy('start', 'asc'),
          limit(5)
        ));
        setUpcomingEvents(upSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // Load past events
        const pastSnap = await getDocs(query(
          collection(db, 'events'),
          where('admin_uid', '==', v.td_uid),
          where('start', '<', now),
          orderBy('start', 'desc'),
          limit(5)
        ));
        setPastEvents(pastSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // QR code
        const url = `${window.location.origin}/venue/${venueId}`;
        const qr = await QRCode.toDataURL(url, { width: 160, margin: 1,
          color: { dark: '#000', light: '#fff' } });
        setQrDataUrl(qr);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [venueId, user?.uid]);

  const toggleFollow = async () => {
    if (!user?.uid || !venueId) return;
    setFollowLoading(true);
    try {
      const venueRef = doc(db, 'venues', venueId);
      if (isFollowing) {
        await updateDoc(venueRef, { followers: arrayRemove(user.uid) });
        setIsFollowing(false);
      } else {
        await updateDoc(venueRef, { followers: arrayUnion(user.uid) });
        setIsFollowing(true);
        // Notify TD of new follower
        if (venue.td_uid && venue.td_uid !== user.uid) {
          await addDoc(collection(db, 'users', venue.td_uid, 'notifications'), {
            type: 'venue_followed',
            recipientUid: venue.td_uid,
            eventId: venueId,
            eventName: venue.name,
            actorName: user.displayName ?? 'A player',
            message: `${user.displayName ?? 'A player'} is now following ${venue.name}`,
            urgent: false, read: false, createdAt: serverTimestamp(),
          });
        }
      }
      setVenue((v: any) => ({
        ...v,
        followers: isFollowing
          ? v.followers?.filter((f: string) => f !== user.uid)
          : [...(v.followers ?? []), user.uid]
      }));
    } catch (e) { console.error(e); }
    setFollowLoading(false);
  };

  const saveEdits = async () => {
    if (!venueId || !isOwner) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'venues', venueId), {
        name: editForm.name,
        license_type: editForm.license_type ?? 'private_home_game',
        house_rules: editForm.house_rules ?? '',
        parking: editForm.parking ?? '',
        food_drinks: editForm.food_drinks ?? '',
        amenities: editForm.amenities ?? '',
        description: editForm.description,
        schedule: editForm.schedule,
        game_type: editForm.game_type,
        buy_in_range: editForm.buy_in_range,
        area: editForm.area,
        city: editForm.city,
        province: editForm.province,
        social: editForm.social ?? {},
        brand_color_primary: editForm.brand_color_primary ?? '#19E6CF',
        brand_color_secondary: editForm.brand_color_secondary ?? '#D4A017',
        updatedAt: serverTimestamp(),
      });
      setVenue((v: any) => ({ ...v, ...editForm }));
      setEditing(false);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  if (loading) return <Center h="400px"><Spinner size="xl" color="teal.400" /></Center>;

  if (!venue) return (
    <Center h="400px" flexDirection="column" gap={4}>
      <Text color="gray.400" fontSize="16px">Venue not found.</Text>
      <Box as="button" px={5} py={3} bg="#052E16" border="1px solid #166534"
        borderRadius={8} fontSize="13px" fontWeight="700" color="#86EFAC"
        fontFamily={F.ui} cursor="pointer" onClick={() => navigate('/find-a-game')}>
        Browse Games →
      </Box>
    </Center>
  );

  const primary = venue.brand_color_primary ?? '#19E6CF';
  const secondary = venue.brand_color_secondary ?? '#D4A017';
  const C = { bg:'#080A0E', card:'#131720', border:'#1E2330', white:'#F0F4FF',
    ink:'#C8D0E0', dim:'#6B7590', green:'#22C55E' };
  const pctds = PCTDS_CONFIG[venue.pctds_status as keyof typeof PCTDS_CONFIG] ?? PCTDS_CONFIG.none;
  const followerCount = venue.followers?.length ?? 0;

  return (
    <Box maxW="780px" mx="auto" px={4} py={8} fontFamily={F.ui}>

      {/* ── VENUE CARD ── */}
      <Box border={`2px solid ${primary}`} borderRadius={12} overflow="hidden" mb={6}>

        {/* Banner */}
        <Box h="120px" position="relative"
          bg={`linear-gradient(135deg, ${primary}22 0%, ${secondary}22 100%)`}
          borderBottom={`1px solid ${C.border}`}>
          {venue.banner_url && (
            <img src={venue.banner_url} alt="banner"
              style={{ width:'100%', height:'100%', objectFit:'cover', opacity:0.6 }} />
          )}
          <Box position="absolute" top={0} left={0} right={0} bottom={0}
            bg={`linear-gradient(135deg, ${primary}11, ${secondary}11)`} />
        </Box>

        {/* Header bar */}
        <Flex bg="#0A0A0A" px={5} py={3} justify="space-between" align="center">
          <Flex align="baseline" gap={0}>
            <Text color="#AAA" fontSize="16px" fontWeight="700" fontFamily={F.ui}>poker</Text>
            <Text color="#FFF" fontSize="16px" fontWeight="700" fontFamily={F.ui}>city</Text>
            <Text color="#444" fontSize="13px" ml="2px">♣</Text>
          </Flex>
          <Flex align="center" gap={3}>
            {venue.pctds_status === 'verified' && (
              <Box px={3} py={1} bg="#0D1F1E" border={`1px solid ${primary}`} borderRadius={4}>
                <Text style={{ fontFamily:F.mono, fontSize:10, color:primary, letterSpacing:'0.18em' }}>
                  ✅ PCTDS VERIFIED
                </Text>
              </Box>
            )}
            {venue.pctds_status === 'applied' && (
              <Box px={3} py={1} bg="#1A1200" border="1px solid #D97706" borderRadius={4}>
                <Text style={{ fontFamily:F.mono, fontSize:10, color:'#D97706', letterSpacing:'0.18em' }}>
                  ⏳ PCTDS PENDING
                </Text>
              </Box>
            )}
          </Flex>
        </Flex>

        {/* Body */}
        <Flex bg="white" p={5} gap={5} align="flex-start">
          {/* Left — logo + QR */}
          <Flex direction="column" align="center" gap={3} flexShrink={0}>
            {venue.logo_url ? (
              <Box w="80px" h="80px" borderRadius={8} overflow="hidden"
                border={`2px solid ${primary}`}>
                <img src={venue.logo_url} alt="logo"
                  style={{ width:'100%', height:'100%', objectFit:'cover' }} />
              </Box>
            ) : (
              <Center w="80px" h="80px" borderRadius={8}
                bg={`${primary}22`} border={`2px solid ${primary}`}>
                <Text style={{ fontSize:32 }}>🃏</Text>
              </Center>
            )}
            {qrDataUrl && (
              <Box border="1px solid #E0E0E0" p={1} borderRadius={4}>
                <img src={qrDataUrl} alt="QR" width={80} height={80} />
              </Box>
            )}
            <Text style={{ fontSize:9, letterSpacing:'0.12em', color:'#888',
              textTransform:'uppercase' }}>Scan to visit</Text>
          </Flex>

          {/* Right — identity */}
          <Flex direction="column" gap={2} flex={1}>
            <Box>
              <Text style={{ fontSize:11, letterSpacing:'0.15em', color:'#888',
                textTransform:'uppercase', marginBottom:4 }}>Venue</Text>
              <Heading style={{ fontFamily:F.ui, fontSize:28, fontWeight:700,
                color:'#1A202C', marginBottom:4 }}>
                {venue.name}
              </Heading>
              {venue.description && (
                <Text style={{ fontFamily:F.ui, fontSize:14, color:'#555', lineHeight:1.6 }}>
                  {venue.description}
                </Text>
              )}
            </Box>

            <Flex gap={4} mt={2} flexWrap="wrap">
              {venue.city && (
                <Box>
                  <Text style={{ fontSize:10, letterSpacing:'0.12em', color:'#888',
                    textTransform:'uppercase' }}>Location</Text>
                  <Text style={{ fontFamily:F.ui, fontSize:13, fontWeight:700, color:'#333' }}>
                    {venue.area ? `${venue.area}, ` : ''}{venue.city}{venue.province ? `, ${venue.province}` : ''}
                  </Text>
                </Box>
              )}
              {venue.schedule && (
                <Box>
                  <Text style={{ fontSize:10, letterSpacing:'0.12em', color:'#888',
                    textTransform:'uppercase' }}>Schedule</Text>
                  <Text style={{ fontFamily:F.ui, fontSize:13, fontWeight:700, color:'#333' }}>
                    {venue.schedule}
                  </Text>
                </Box>
              )}
              {venue.game_type && (
                <Box>
                  <Text style={{ fontSize:10, letterSpacing:'0.12em', color:'#888',
                    textTransform:'uppercase' }}>Game</Text>
                  <Text style={{ fontFamily:F.ui, fontSize:13, fontWeight:700, color:'#333' }}>
                    {venue.game_type}{venue.buy_in_range ? ` · ${venue.buy_in_range}` : ''}
                  </Text>
                </Box>
              )}
            </Flex>

            {/* Stats */}
            <Flex gap={5} mt={3} pt={3} borderTop="1px solid #F0F0F0">
              {[
                { label:'Events Hosted', value: venue.event_count ?? 0 },
                { label:'Avg Rating', value: venue.avg_rating > 0 ? `★ ${venue.avg_rating?.toFixed(1)}` : '—' },
                { label:'Followers', value: followerCount },
              ].map(s => (
                <Flex key={s.label} direction="column" align="center" gap={1}>
                  <Text style={{ fontSize:20, fontWeight:700, color:'#1A202C', fontFamily:'mono' }}>
                    {s.value}
                  </Text>
                  <Text style={{ fontSize:9, letterSpacing:'0.15em', textTransform:'uppercase', color:'#888' }}>
                    {s.label}
                  </Text>
                </Flex>
              ))}
            </Flex>
          </Flex>
        </Flex>

        {/* Footer strip */}
        <Flex bg="#F5F5F5" px={5} py={2} justify="space-between" align="center"
          borderTop="1px solid #E0E0E0">
          <Text style={{ fontSize:10, letterSpacing:'0.2em', color:'#888', fontFamily:'mono' }}>
            POKERCITY VENUE · {venue.name?.toUpperCase()}
          </Text>
          <Text style={{ fontSize:10, color:'#888' }}>pokercity.com</Text>
        </Flex>
      </Box>

      {/* ── ACTION BUTTONS ── */}
      <Flex gap={3} mb={6} flexWrap="wrap">
        {user && !isOwner && (
          <Box as="button" px={5} py={3}
            bg={isFollowing ? '#0D1F1E' : C.card}
            border={`1px solid ${isFollowing ? primary : C.border}`}
            borderRadius={8} fontSize="13px" fontWeight="700"
            color={isFollowing ? primary : C.dim}
            fontFamily={F.ui} cursor={followLoading ? 'wait' : 'pointer'}
            onClick={toggleFollow}>
            {followLoading ? '...' : isFollowing ? '✓ Following' : '+ Follow'}
          </Box>
        )}
        {venue.social?.facebook && (
          <Box as="button" px={4} py={3} bg={C.card} border={`1px solid ${C.border}`}
            borderRadius={8} fontSize="13px" fontWeight="700" color={C.dim}
            fontFamily={F.ui} cursor="pointer"
            onClick={() => window.open(venue.social.facebook, '_blank')}>
            Facebook →
          </Box>
        )}
        {venue.social?.discord && (
          <Box as="button" px={4} py={3} bg={C.card} border={`1px solid ${C.border}`}
            borderRadius={8} fontSize="13px" fontWeight="700" color={C.dim}
            fontFamily={F.ui} cursor="pointer"
            onClick={() => window.open(venue.social.discord, '_blank')}>
            Discord →
          </Box>
        )}
        {isOwner && (
          <>
            <Box as="button" px={5} py={3} bg="#0D1520" border={`1px solid ${primary}`}
              borderRadius={8} fontSize="13px" fontWeight="700" color={primary}
              fontFamily={F.ui} cursor="pointer" onClick={() => setEditing(!editing)}>
              {editing ? 'Cancel Edit' : '✏ Edit Venue'}
            </Box>
            {venue.pctds_status === 'none' && (
              <Box as="button" px={5} py={3} bg="#1A0F00" border={`1px solid ${secondary}`}
                borderRadius={8} fontSize="13px" fontWeight="700" color={secondary}
                fontFamily={F.ui} cursor="pointer"
                onClick={() => navigate(`/venue/${venueId}/apply`)}>
                Apply for PCTDS →
              </Box>
            )}
          </>
        )}
      </Flex>

      {/* ── EDIT FORM ── */}
      {editing && isOwner && (
        <Box bg={C.card} border={`1px solid ${primary}`} borderRadius={10} p={5} mb={6}>
          <Text style={{ fontFamily:F.mono, fontSize:11, color:primary,
            letterSpacing:'0.2em', marginBottom:16 }}>EDIT VENUE DETAILS</Text>
          <Flex direction="column" gap={4}>
            {[
              { label:'Venue Name', field:'name', placeholder:'e.g. Wayne\'s Thursday Night' },
              { label:'Description', field:'description', placeholder:'Tell players about your game...', multiline:true },
              { label:'Schedule', field:'schedule', placeholder:'e.g. Every Thursday 7:00 PM' },
              { label:'Game Type', field:'game_type', placeholder:'e.g. Texas Hold\'em' },
              { label:'Buy-in Range', field:'buy_in_range', placeholder:'e.g. $50-$100' },
              { label:'Area / Neighbourhood', field:'area', placeholder:'e.g. River Heights' },
              { label:'City', field:'city', placeholder:'e.g. Winnipeg' },
              { label:'Province', field:'province', placeholder:'e.g. MB' },
            ].map(f => (
              <Box key={f.field}>
                <Text style={{ fontFamily:F.mono, fontSize:10, color:'#6B7590',
                  letterSpacing:'0.15em', marginBottom:6, textTransform:'uppercase' }}>
                  {f.label}
                </Text>
                {f.multiline ? (
                  <Box as="textarea" rows={3}
                    value={editForm[f.field] ?? ''}
                    onChange={(e:any) => setEditForm((ef:any) => ({...ef, [f.field]:e.target.value}))}
                    placeholder={f.placeholder}
                    style={{ display:'block', width:'100%', padding:'10px 12px',
                      background:'#0D1118', border:`1px solid ${C.border}`,
                      borderRadius:6, color:'#F0F4FF', fontSize:14,
                      fontFamily:F.ui, resize:'none' as any, boxSizing:'border-box' as any }} />
                ) : (
                  <input value={editForm[f.field] ?? ''}
                    onChange={e => setEditForm((ef:any) => ({...ef, [f.field]:e.target.value}))}
                    placeholder={f.placeholder}
                    style={{ display:'block', width:'100%', padding:'10px 12px',
                      background:'#0D1118', border:`1px solid ${C.border}`,
                      borderRadius:6, color:'#F0F4FF', fontSize:14,
                      fontFamily:F.ui, boxSizing:'border-box' as any }} />
                )}
              </Box>
            ))}

            {/* License type */}
            <Box>
              <Text style={{ fontFamily:F.mono, fontSize:10, color:'#6B7590', letterSpacing:'0.15em', marginBottom:6, textTransform:'uppercase' as const }}>License Type</Text>
              <select style={{ display:'block',width:'100%',padding:'10px 12px',background:'#0D1118',border:`1px solid ${C.border}`,borderRadius:6,color:C.white,fontSize:14,fontFamily:F.ui,cursor:'pointer' }}
                value={editForm.license_type ?? 'private_home_game'}
                onChange={e => setEditForm((ef:any) => ({...ef, license_type:e.target.value}))}>
                {[['private_home_game','🏠 Private Home Game'],['charitable','❤️ Charitable / Fundraiser'],['pctds_sanctioned','✅ PCTDS Sanctioned'],['licensed_venue','🏢 Licensed Venue'],['casino_night','🎰 Casino Night'],['corporate','💼 Corporate Event'],['other','Other']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Box>

            {/* House rules */}
            <Box>
              <Text style={{ fontFamily:F.mono, fontSize:10, color:'#6B7590', letterSpacing:'0.15em', marginBottom:6, textTransform:'uppercase' as const }}>House Rules</Text>
              <Box as="textarea" rows={4} value={editForm.house_rules ?? ''}
                onChange={(e:any) => setEditForm((ef:any) => ({...ef, house_rules:e.target.value}))}
                placeholder={"One rule per line e.g.:\nRebuys until level 6\nDealer tips encouraged\nNo phones at table\nBYOB — no alcohol provided"}
                style={{ display:'block',width:'100%',padding:'10px 12px',background:'#0D1118',border:`1px solid ${C.border}`,borderRadius:6,color:C.white,fontSize:14,fontFamily:F.ui,resize:'none' as any,boxSizing:'border-box' as any }} />
              <Text style={{ fontFamily:F.ui, fontSize:11, color:'#6B7590', marginTop:4 }}>One rule per line. These appear at the top of your room page and all event pages.</Text>
            </Box>

            {/* Venue info */}
            {[{label:'Parking',field:'parking',placeholder:'e.g. Free street parking on Oak St, paid lot 1 block east'},{label:'Food & Drinks',field:'food_drinks',placeholder:'e.g. BYOB welcome, snacks provided, no hot food'},{label:'Amenities',field:'amenities',placeholder:'e.g. Professional felt tables, clay chips, dedicated poker room'}].map(f => (
              <Box key={f.field}>
                <Text style={{ fontFamily:F.mono, fontSize:10, color:'#6B7590', letterSpacing:'0.15em', marginBottom:6, textTransform:'uppercase' as const }}>{f.label}</Text>
                <Box as="textarea" rows={2} value={editForm[f.field] ?? ''}
                  onChange={(e:any) => setEditForm((ef:any) => ({...ef, [f.field]:e.target.value}))}
                  placeholder={f.placeholder}
                  style={{ display:'block',width:'100%',padding:'10px 12px',background:'#0D1118',border:`1px solid ${C.border}`,borderRadius:6,color:C.white,fontSize:14,fontFamily:F.ui,resize:'none' as any,boxSizing:'border-box' as any }} />
              </Box>
            ))}

            {/* Brand colors */}
            <Flex gap={4}>
              <Box>
                <Text style={{ fontFamily:F.mono, fontSize:10, color:'#6B7590',
                  letterSpacing:'0.15em', marginBottom:6, textTransform:'uppercase' }}>
                  Primary Color
                </Text>
                <Flex align="center" gap={2}>
                  <input type="color" value={editForm.brand_color_primary ?? '#19E6CF'}
                    onChange={e => setEditForm((ef:any) => ({...ef, brand_color_primary:e.target.value}))}
                    style={{ width:40, height:36, borderRadius:4, border:'none', cursor:'pointer' }} />
                  <Text style={{ fontFamily:F.mono, fontSize:12, color:'#6B7590' }}>
                    {editForm.brand_color_primary ?? '#19E6CF'}
                  </Text>
                </Flex>
              </Box>
              <Box>
                <Text style={{ fontFamily:F.mono, fontSize:10, color:'#6B7590',
                  letterSpacing:'0.15em', marginBottom:6, textTransform:'uppercase' }}>
                  Secondary Color
                </Text>
                <Flex align="center" gap={2}>
                  <input type="color" value={editForm.brand_color_secondary ?? '#D4A017'}
                    onChange={e => setEditForm((ef:any) => ({...ef, brand_color_secondary:e.target.value}))}
                    style={{ width:40, height:36, borderRadius:4, border:'none', cursor:'pointer' }} />
                  <Text style={{ fontFamily:F.mono, fontSize:12, color:'#6B7590' }}>
                    {editForm.brand_color_secondary ?? '#D4A017'}
                  </Text>
                </Flex>
              </Box>
            </Flex>

            {/* Social links */}
            <Text style={{ fontFamily:F.mono, fontSize:10, color:'#6B7590',
              letterSpacing:'0.15em', textTransform:'uppercase' }}>Social Links</Text>
            {['facebook','instagram','twitter','discord'].map(platform => (
              <Flex key={platform} align="center" gap={3}>
                <Text style={{ fontFamily:F.mono, fontSize:11, color:'#6B7590',
                  width:80, textTransform:'capitalize' }}>{platform}</Text>
                <input value={editForm.social?.[platform] ?? ''}
                  onChange={e => setEditForm((ef:any) => ({
                    ...ef, social: {...(ef.social ?? {}), [platform]: e.target.value}
                  }))}
                  placeholder={`https://${platform}.com/...`}
                  style={{ flex:1, padding:'8px 12px', background:'#0D1118',
                    border:`1px solid ${C.border}`, borderRadius:6, color:'#F0F4FF',
                    fontSize:13, fontFamily:F.ui }} />
              </Flex>
            ))}

            <Box as="button" px={6} py={3} bg="#052E16" border="1px solid #166534"
              borderRadius={8} fontSize="14px" fontWeight="700" color="#86EFAC"
              fontFamily={F.ui} cursor={saving?'wait':'pointer'} opacity={saving?0.7:1}
              onClick={saveEdits}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Box>
          </Flex>
        </Box>
      )}

      {/* ── UPCOMING EVENTS ── */}
      {upcomingEvents.length > 0 && (
        <Box bg={C.card} border={`1px solid ${C.border}`} borderRadius={10}
          overflow="hidden" mb={4}>
          <Box bg="#0D1520" px={4} py={3} borderBottom={`1px solid ${C.border}`}>
            <Text style={{ fontFamily:F.mono, fontSize:11, color:primary,
              letterSpacing:'0.2em' }}>UPCOMING EVENTS</Text>
          </Box>
          {upcomingEvents.map((ev, i) => (
            <Flex key={ev.id} px={4} py={3} justify="space-between" align="center"
              borderBottom={i < upcomingEvents.length-1 ? `1px solid ${C.border}` : 'none'}
              bg={C.card} cursor="pointer" onClick={() => navigate(`/event/${ev.id}`)}>
              <Box>
                <Text style={{ fontFamily:F.ui, fontSize:14, fontWeight:600, color:'#F0F4FF' }}>
                  {ev.name ?? 'Poker Event'}
                </Text>
                <Text style={{ fontFamily:F.ui, fontSize:12, color:'#6B7590' }}>
                  {ev.start ? new Date(ev.start).toLocaleDateString('en-CA',
                    { weekday:'short', month:'short', day:'numeric', hour:'numeric', minute:'2-digit' }) : ''}
                </Text>
              </Box>
              <Flex gap={2} align="center">
                {ev.buy_in > 0 && (
                  <Box px={2} py={1} bg="#0D1118" border={`1px solid ${C.border}`} borderRadius={4}>
                    <Text style={{ fontFamily:F.mono, fontSize:11, color:'#C8D0E0' }}>
                      ${ev.buy_in}
                    </Text>
                  </Box>
                )}
                <Text style={{ fontFamily:F.mono, fontSize:11, color:primary }}>→</Text>
              </Flex>
            </Flex>
          ))}
        </Box>
      )}

      {/* ── PAST EVENTS ── */}
      {pastEvents.length > 0 && (
        <Box bg={C.card} border={`1px solid ${C.border}`} borderRadius={10}
          overflow="hidden" mb={4}>
          <Box bg="#0D1118" px={4} py={3} borderBottom={`1px solid ${C.border}`}>
            <Text style={{ fontFamily:F.mono, fontSize:11, color:'#6B7590',
              letterSpacing:'0.2em' }}>RECENT EVENTS</Text>
          </Box>
          {pastEvents.map((ev, i) => (
            <Flex key={ev.id} px={4} py={3} justify="space-between" align="center"
              borderBottom={i < pastEvents.length-1 ? `1px solid ${C.border}` : 'none'}
              bg={C.card}>
              <Box>
                <Text style={{ fontFamily:F.ui, fontSize:14, color:'#C8D0E0' }}>
                  {ev.name ?? 'Poker Event'}
                </Text>
                <Text style={{ fontFamily:F.ui, fontSize:12, color:'#6B7590' }}>
                  {ev.start ? new Date(ev.start).toLocaleDateString('en-CA',
                    { month:'short', day:'numeric', year:'numeric' }) : ''}
                </Text>
              </Box>
              {ev.avg_rating > 0 && (
                <Text style={{ fontFamily:F.mono, fontSize:12, color:'#D4A017' }}>
                  ★ {ev.avg_rating?.toFixed(1)}
                </Text>
              )}
            </Flex>
          ))}
        </Box>
      )}

      {/* ── TD INFO ── */}
      {tdProfile && (
        <Flex align="center" gap={3} mt={4} cursor="pointer"
          onClick={() => navigate(`/passport/${venue.td_uid}`)}>
          <Avatar size="sm" src={tdProfile.avatar_url} name={tdProfile.display_name} />
          <Box>
            <Text style={{ fontFamily:F.ui, fontSize:13, color:'#6B7590' }}>Hosted by</Text>
            <Text style={{ fontFamily:F.ui, fontSize:14, fontWeight:700, color:'#F0F4FF' }}>
              {tdProfile.display_name ?? tdProfile.alias ?? 'Tournament Director'}
            </Text>
          </Box>
          <Text style={{ fontFamily:F.mono, fontSize:11, color:'#6B7590', marginLeft:'auto' }}>
            View Passport →
          </Text>
        </Flex>
      )}
    </Box>
  );
};

export default VenueProfilePage;
